import { createRouter, createWebHistory, type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router'
import { useUserStore } from '../stores/user'
import { resolveSession, guestOnlyGuard } from './authGuards'
import { sweepRoutes, sweepPublicRoutes } from './sweepRoutes'
import { trackPageView } from '../services/analytics'
import { setRouteTitle } from '../utils/pageTitle'
import api from '../services/api'
import { CURRENT_TERMS_VERSION } from '../config/terms'
import Login from '../views/Login.vue'
import Signup from '../views/Signup.vue'
import AuthCallback from '../views/AuthCallback.vue'
import SupplierDashboard from '../views/SupplierDashboard.vue'
import SupplierCompanyProfile from '../views/SupplierCompanyProfile.vue'
import SupplierMetricsCatalog from '../views/SupplierMetricsCatalog.vue'
import AdvisorSupplierDirectory from '../views/AdvisorSupplierDirectory.vue'
import AdvisorSupplierProfile from '../views/AdvisorSupplierProfile.vue'
// AdvisorDashboard import removed — /advisor/dashboard now redirects to /advisor/today
import SupplierProducts from '../views/SupplierProducts.vue'

// Stubs
import SupplierPromotions from '../views/stubs/SupplierPromotions.vue'
import SupplierTraining from '../views/stubs/SupplierTraining.vue'
import AdvisorProducts from '../views/AdvisorProductDirectory.vue'
import AdvisorTraining from '../views/stubs/AdvisorTraining.vue'
import ResourcesDocs from '../views/stubs/ResourcesDocs.vue'
import ResourcesSupport from '../views/stubs/ResourcesSupport.vue'
import ChangePassword from '../views/ChangePassword.vue'

// Custom Auth Guard that supports both Auth0 SDK and our local dev ROPG token
const customAuthGuard = async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
    if (await resolveSession()) return next()

    // Not authenticated — redirect to our custom login page
    return next({
        path: '/login',
        query: to.fullPath !== '/' ? { returnTo: to.fullPath } : undefined
    })
}

const router = createRouter({
    history: createWebHistory(),
    routes: [
        // --- AUTH ROUTES ---
        {
            path: '/',
            name: 'home',
            component: Login,
            beforeEnter: guestOnlyGuard,
            meta: { title: 'Sign in' }
        },
        {
            path: '/login',
            name: 'login',
            component: Login,
            beforeEnter: guestOnlyGuard,
            meta: { title: 'Sign in' }
        },
        {
            path: '/signup',
            name: 'signup',
            component: Signup,
            beforeEnter: customAuthGuard,
            meta: { title: 'Sign up' }
        },
        {
            path: '/signup/complete-oauth',
            name: 'oauth-complete-signup',
            component: () => import('../views/OAuthCompleteSignup.vue'),
            meta: { title: 'Complete sign-up' }
        },
        {
            path: '/auth/callback',
            name: 'auth-callback',
            component: AuthCallback,
            meta: { title: 'Signing in…' }
        },
        {
            path: '/claim',
            name: 'claim',
            redirect: to => ({ path: '/signup', query: { claim: to.query.code } })
        },
        {
            path: '/invite',
            name: 'invite',
            component: () => import('../views/AcceptInvite.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Accept invitation' }
        },
        {
            path: '/change-password',
            name: 'change-password',
            component: ChangePassword,
            beforeEnter: customAuthGuard,
            meta: { title: 'Change password', requiresAuth: true }
        },
        {
            path: '/terms',
            name: 'terms',
            component: () => import('../views/AdvisorTerms.vue'),
            beforeEnter: customAuthGuard,
            meta: { requiresAuth: true, title: 'Terms & Conditions' }
        },

        // --- ONBOARDING ROUTES ---
        {
            path: '/advisor/onboarding',
            name: 'advisor-onboarding',
            component: () => import('../views/AdvisorOnboarding.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Get started' }
        },
        {
            path: '/supplier/onboarding',
            name: 'supplier-onboarding',
            component: () => import('../views/SupplierOnboarding.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Get started' }
        },

        // --- ADMIN ROUTES ---
        {
            path: '/admin',
            name: 'admin-dashboard',
            component: () => import('../views/admin/AdminDashboard.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Dashboard – Admin' }
        },
        {
            path: '/admin/suppliers',
            name: 'admin-suppliers',
            component: () => import('../views/admin/AdminSuppliers.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Suppliers – Admin' }
        },
        {
            path: '/admin/suppliers/:supplierId',
            name: 'admin-supplier-detail',
            component: () => import('../views/admin/AdminSupplierDetail.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Suppliers – Admin' }
        },
        {
            path: '/admin/suppliers/:supplierId/intelligence',
            name: 'admin-supplier-intelligence',
            component: () => import('../views/admin/AdminSupplierIntelligence.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Intelligence – Admin' }
        },
        {
            path: '/admin/users',
            name: 'admin-users',
            component: () => import('../views/admin/AdminUsers.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Users – Admin' }
        },
        {
            path: '/admin/channel-managers',
            name: 'admin-channel-managers',
            component: () => import('../views/admin/AdminChannelManagers.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Channel Managers – Admin' }
        },
        {
            path: '/admin/sweep-suppliers',
            name: 'admin-sweep-suppliers',
            component: () => import('../views/admin/AdminSweepSuppliers.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Sweep Suppliers – Admin' }
        },
        {
            path: '/admin/ai-diagnostics',
            name: 'admin-ai-diagnostics',
            component: () => import('../views/admin/AdminAiDiagnostics.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'AI Diagnostics – Admin' }
        },
        {
            path: '/admin/ai-models',
            name: 'admin-ai-models',
            component: () => import('../views/admin/AdminAiModels.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'AI Models – Admin' }
        },
        {
            path: '/admin/ai-models/:id',
            name: 'admin-ai-model-detail',
            component: () => import('../views/admin/AdminAiModelDetail.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'AI Models – Admin' }
        },
        {
            path: '/admin/ai-prompts',
            name: 'admin-ai-prompts',
            component: () => import('../views/admin/AdminAiPrompts.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'AI Prompts – Admin' }
        },
        {
            path: '/admin/tsds',
            name: 'admin-tsds',
            component: () => import('../views/admin/AdminTSDs.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'TSDs – Admin' }
        },
        {
            path: '/admin/tsd-contacts',
            name: 'admin-tsd-contacts',
            component: () => import('../views/admin/AdminTsdContacts.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'TSD Contacts – Admin' }
        },
        {
            path: '/admin/taxonomy',
            name: 'admin-taxonomy',
            component: () => import('../views/admin/AdminTaxonomy.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Taxonomy – Admin' }
        },
        {
            path: '/admin/quotes',
            name: 'admin-quote-queue',
            component: () => import('../views/admin/AdminQuoteQueue.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Quote Queue – Admin' }
        },
        {
            path: '/admin/mdf',
            name: 'admin-mdf-activity',
            component: () => import('../views/admin/AdminMdfActivity.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'MDF Activity – Admin' }
        },
        {
            path: '/admin/invite-codes',
            name: 'admin-invite-codes',
            component: () => import('../views/admin/AdminInviteCodes.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Invite Codes – Admin' }
        },
        {
            path: '/admin/create-user',
            name: 'admin-create-user',
            component: () => import('../views/admin/AdminCreateUser.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Create User – Admin' }
        },
        {
            path: '/admin/analytics',
            name: 'admin-analytics',
            component: () => import('../views/admin/AdminAnalytics.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Analytics – Admin' }
        },
        {
            path: '/admin/analytics/users/:userId',
            name: 'admin-analytics-user',
            component: () => import('../views/admin/AdminAnalyticsUserDetail.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Analytics – Admin' }
        },
        {
            path: '/admin/activity',
            name: 'admin-activity-log',
            component: () => import('../views/admin/AdminActivityLog.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Activity Log – Admin' }
        },
        {
            path: '/admin/ai-generations',
            name: 'admin-ai-generation-log',
            component: () => import('../views/admin/AdminAiGenerationLog.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'AI Generation Log – Admin' }
        },
        {
            path: '/admin/legal',
            name: 'admin-legal-docs',
            component: () => import('../views/admin/AdminLegalDocs.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Legal Documents – Admin' }
        },
        {
            path: '/admin/landscape-relevance',
            name: 'admin-landscape-relevance',
            component: () => import('../views/admin/AdminLandscapeRelevance.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Landscape Relevance – Admin' }
        },

        // --- SUPPLIER ROUTES ---
        {
            path: '/supplier',
            name: 'supplier-dashboard',
            component: SupplierDashboard,
            beforeEnter: customAuthGuard,
            meta: { title: 'Dashboard', requiredTier: 'BASIC' }
        },
        {
            path: '/supplier/profile',
            name: 'supplier-profile',
            component: SupplierCompanyProfile,
            beforeEnter: customAuthGuard,
            meta: { title: 'Company Profile' }
        },
        {
            path: '/supplier/products',
            name: 'supplier-products',
            component: SupplierProducts,
            beforeEnter: customAuthGuard,
            meta: { title: 'Products' }
        },
        {
            path: '/supplier/solution-map',
            name: 'supplier-solution-map',
            component: () => import('../views/SupplierSolutionMap.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Solution Map', requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/products/new',
            name: 'supplier-product-create',
            component: () => import('../views/SupplierProductEditor.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'New product – Products', requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/products/:productId/edit',
            name: 'supplier-product-edit',
            component: () => import('../views/SupplierProductEditor.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Edit product – Products', requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/promotions',
            name: 'supplier-promotions',
            component: SupplierPromotions,
            beforeEnter: customAuthGuard,
            meta: { title: 'Promotions' }
        },
        {
            path: '/supplier/mdf/requests',
            name: 'supplier-mdf-requests',
            component: () => import('../views/SupplierMdfRequests.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'MDF Requests' }
        },
        {
            path: '/supplier/mdf/requests/:id',
            name: 'supplier-mdf-detail',
            component: () => import('../views/SupplierMdfRequestDetail.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'MDF Requests' }
        },
        {
            path: '/supplier/mdf/offerings',
            name: 'supplier-mdf-offerings',
            component: () => import('../views/SupplierMdfOfferings.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'MDF Offerings' }
        },
        {
            path: '/supplier/mdf/offerings/new',
            name: 'supplier-mdf-offering-new',
            component: () => import('../views/SupplierMdfOfferingForm.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'New offering – MDF Offerings' }
        },
        {
            path: '/supplier/mdf/offerings/:id/edit',
            name: 'supplier-mdf-offering-edit',
            component: () => import('../views/SupplierMdfOfferingForm.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Edit offering – MDF Offerings' }
        },
        {
            path: '/supplier/competitive/suppliers',
            name: 'supplier-competitor-explorer',
            component: () => import('../views/SupplierCompetitorExplorer.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Competitor Explorer' }
        },
        {
            path: '/supplier/competitive/products',
            name: 'supplier-product-explorer',
            component: () => import('../views/SupplierProductExplorer.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Product Explorer' }
        },
        {
            path: '/supplier/competitive-landscape/:competitorId',
            name: 'supplier-competitor-analysis',
            component: () => import('../views/SupplierCompetitorAnalysis.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Competitive Landscape', requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/competitive-landscape',
            name: 'supplier-competitive-landscape',
            component: () => import('../views/SupplierCompetitiveLandscape.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Competitive Landscape', requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/training',
            name: 'supplier-training',
            component: SupplierTraining,
            beforeEnter: customAuthGuard,
            meta: { title: 'Content Library', requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/training/new',
            name: 'supplier-training-create',
            component: () => import('../views/SupplierTrainingEditor.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'New content – Content Library', requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/training/:id/edit',
            name: 'supplier-training-edit',
            component: () => import('../views/SupplierTrainingEditor.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Edit content – Content Library', requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/metrics',
            name: 'supplier-metrics',
            component: SupplierMetricsCatalog,
            beforeEnter: customAuthGuard,
            meta: { title: 'Metrics Library', requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/settings',
            name: 'supplier-settings',
            component: () => import('../views/SupplierSettings.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Settings', requiredTier: 'BASIC' }
        },
        {
            path: '/supplier/contacts',
            name: 'supplier-contacts',
            component: () => import('../views/SupplierContacts.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Channel Team' }
        },
        {
            path: '/supplier/team',
            name: 'supplier-employees',
            component: () => import('../views/SupplierEmployees.vue'),
            // Team management (incl. removing former employees) is open to all
            // tiers — it's an account-hygiene action, not a premium feature.
            beforeEnter: customAuthGuard,
            meta: { title: 'Team Members' }
        },
        {
            path: '/supplier/products/:productId',
            name: 'supplier-product-detail',
            component: () => import('../views/SupplierProductDetail.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Products' }
        },
        // Quote Routes (Phase 9)
        {
            path: '/supplier/quotes',
            name: 'supplier-quote-queue',
            component: () => import('../views/SupplierQuoteQueue.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Quotes', requiredTier: 'ENTERPRISE' }
        },
        {
            path: '/supplier/quotes/:quoteId',
            name: 'supplier-quote-detail',
            component: () => import('../views/SupplierQuoteDetail.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Quotes', requiredTier: 'ENTERPRISE' }
        },

        // --- ADVISOR ROUTES ---

        // Advisor Workspace (v3b)
        // WorkspacePipeline retired — advisors work deal-by-deal from an
        // account. Redirect preserves any bookmarked URL.
        { path: '/advisor/workspace/deals', redirect: '/advisor/today' },
        {
            path: '/advisor/workspace/quick-match',
            name: 'workspace-quick-match',
            component: () => import('../views/WorkspaceQuickMatch.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Quick Match' }
        },
        {
            path: '/advisor/workspace/accounts',
            name: 'workspace-accounts',
            component: () => import('../views/WorkspaceAccountsList.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Accounts' }
        },
        {
            path: '/advisor/workspace/accounts/new',
            name: 'workspace-new-account',
            component: () => import('../views/WorkspaceNewAccount.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'New account – Accounts' }
        },
        {
            path: '/advisor/workspace/accounts/:id',
            name: 'workspace-account',
            component: () => import('../views/WorkspaceAccount.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Accounts' }
        },
        {
            path: '/advisor/workspace/accounts/:accountId/context',
            name: 'workspace-account-context',
            component: () => import('../views/WorkspaceAccountContext.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Context – Accounts' }
        },
        {
            path: '/advisor/workspace/accounts/:accountId/meeting',
            name: 'workspace-meeting-companion',
            component: () => import('../views/WorkspaceMeetingCompanion.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Meeting companion – Accounts' }
        },
        {
            path: '/advisor/workspace/accounts/:accountId/briefings',
            name: 'workspace-briefings',
            component: () => import('../views/WorkspaceBriefings.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Briefings – Accounts' }
        },
        {
            path: '/advisor/workspace/accounts/:accountId/briefings/:briefingId',
            name: 'workspace-briefing',
            component: () => import('../views/WorkspaceBriefing.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Briefing – Accounts' }
        },
        {
            path: '/advisor/workspace/accounts/:accountId/post-meeting',
            name: 'workspace-post-meeting',
            component: () => import('../views/WorkspacePostMeeting.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Post-meeting – Accounts' }
        },
        {
            path: '/advisor/workspace/accounts/:accountId/questions',
            name: 'workspace-account-questions',
            component: () => import('../views/WorkspaceAccountQuestions.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Questions – Accounts' }
        },
        {
            path: '/advisor/workspace/accounts/:accountId/questions/print',
            name: 'workspace-account-questions-print',
            component: () => import('../views/WorkspaceAccountQuestionsPrint.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Questions (print) – Accounts' }
        },
        {
            path: '/advisor/workspace/accounts/:accountId/prep',
            name: 'workspace-meeting-prep',
            component: () => import('../views/WorkspaceMeetingPrep.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Meeting prep – Accounts' }
        },
        {
            path: '/advisor/workspace/accounts/:accountId/suppliers/:supplierId/analysis',
            name: 'workspace-account-supplier-analysis',
            component: () => import('../views/WorkspaceSupplierAnalysis.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Supplier analysis – Accounts' }
        },
        {
            path: '/advisor/workspace/accounts/:accountId/solutions/:solutionId',
            name: 'workspace-solution',
            component: () => import('../views/WorkspaceSolutionWorkspace.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Solution – Accounts' }
        },
        {
            path: '/advisor/workspace/accounts/:id/create-deal',
            name: 'workspace-create-deal',
            component: () => import('../views/WorkspaceCreateDeal.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'New deal – Accounts' }
        },
        {
            path: '/advisor/workspace/deals/:id',
            name: 'workspace-deal',
            component: () => import('../views/WorkspaceDeal.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Deals' }
        },
        {
            path: '/advisor/workspace/deals/:id/context',
            name: 'workspace-deal-context',
            component: () => import('../views/WorkspaceDealContext.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Context – Deals' }
        },
        // Phase 1C: Solution Track Lifecycle
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId',
            redirect: to => `/advisor/workspace/deals/${to.params.dealId}/tracks/${to.params.trackId}/technology-discovery`,
        },
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId/quote-request',
            name: 'workspace-quote-request',
            component: () => import('../views/WorkspaceQuoteRequest.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Quote request – Deals' }
        },
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId/registration',
            name: 'workspace-deal-registration',
            component: () => import('../views/WorkspaceDealRegistration.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Deal registration – Deals' }
        },
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId/quotes',
            name: 'workspace-quote-management',
            component: () => import('../views/WorkspaceQuoteManagement.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Quotes – Deals' }
        },
        {
            path: '/advisor/workspace/deals/:dealId/proposal',
            name: 'workspace-proposal',
            component: () => import('../views/WorkspaceProposal.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Proposal – Deals' }
        },
        {
            path: '/advisor/workspace/deals/:dealId/config-builder',
            name: 'workspace-config-builder',
            component: () => import('../views/WorkspaceConfigBuilder.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Configuration builder – Deals' }
        },
        {
            path: '/advisor/workspace/deals/:dealId/config-compare',
            name: 'workspace-config-compare',
            component: () => import('../views/WorkspaceConfigCompare.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Compare configurations – Deals' }
        },
        // Phase 2-4: Discovery, Signals, Prep Brief, AI Companion
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId/technology-discovery',
            name: 'workspace-tech-discovery',
            component: () => import('../views/WorkspaceTechDiscovery.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Technology discovery – Deals' }
        },
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId/suppliers/:supplierId/analysis',
            name: 'workspace-supplier-analysis',
            component: () => import('../views/WorkspaceSupplierAnalysis.vue'),
            props: true,
            beforeEnter: customAuthGuard,
            meta: { title: 'Supplier analysis – Deals' }
        },
        {
            path: '/advisor/workspace/deals/:dealId/signals',
            name: 'workspace-signals',
            component: () => import('../views/WorkspaceSignalDashboard.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Signals – Deals' }
        },
        {
            path: '/advisor/workspace/deals/:dealId/prep-brief',
            name: 'workspace-prep-brief',
            component: () => import('../views/WorkspacePrepBrief.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Prep brief – Deals' }
        },
        {
            path: '/advisor/workspace/deals/:dealId/ai-companion',
            name: 'workspace-ai-companion',
            component: () => import('../views/WorkspaceAiCompanion.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'AI companion – Deals' }
        },
        // Route removed — answer confirmation is now inline on activity detail
        // {
        //     path: '/advisor/workspace/deals/:dealId/confirm-answers',
        //     name: 'workspace-answer-confirmation',
        //     component: () => import('../views/WorkspaceAnswerConfirmation.vue'),
        //     beforeEnter: customAuthGuard
        // },
        {
            path: '/advisor/workspace/accounts/:accountId/activities/:activityId',
            name: 'workspace-activity-detail',
            component: () => import('../views/WorkspaceActivityDetail.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Activity – Accounts' }
        },
        {
            path: '/advisor/workspace/deals/:dealId/tracks/:trackId/solution-discovery',
            name: 'workspace-solution-discovery',
            component: () => import('../views/WorkspaceSolutionDiscovery.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Solution discovery – Deals' }
        },
        {
            path: '/advisor/workspace/tsd-contacts',
            name: 'workspace-tsd-contacts',
            component: () => import('../views/WorkspaceTsdContacts.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'TSD Contacts' }
        },

        {
            path: '/advisor/today',
            name: 'advisor-today',
            component: () => import('../views/AdvisorToday.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Today' }
        },
        {
            path: '/advisor/pipeline',
            name: 'advisor-pipeline',
            component: () => import('../views/AdvisorPipeline.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Pipeline' }
        },
        {
            path: '/advisor/deal-intelligence',
            name: 'advisor-deal-intelligence',
            component: () => import('../views/AdvisorDealIntelligence.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Deal intelligence' }
        },
        {
            path: '/advisor/community',
            name: 'advisor-community',
            component: () => import('../views/AdvisorCommunity.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Community' }
        },
        // Legacy redirects
        { path: '/advisor/pipeline', redirect: '/advisor/deal-intelligence' },
        { path: '/advisor/registrations', redirect: '/advisor/deal-intelligence' },
        { path: '/advisor/actions', redirect: '/advisor/today' },
        // Legacy: /advisor/dashboard redirects to /advisor/today
        { path: '/advisor/dashboard', redirect: '/advisor/today' },
        {
            path: '/advisor',
            redirect: '/advisor/today',
            beforeEnter: customAuthGuard
        },
        {
            path: '/advisor/suppliers',
            name: 'advisor-supplier-directory',
            component: AdvisorSupplierDirectory,
            beforeEnter: customAuthGuard,
            meta: { title: 'Supplier Directory' }
        },
        {
            path: '/advisor/promotions',
            name: 'advisor-promotions',
            component: () => import('../views/AdvisorPromotions.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Promotions' }
        },
        {
            path: '/advisor/mdf/requests',
            name: 'advisor-mdf-list',
            component: () => import('../views/advisor/AdvisorMdfRequests.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'MDF Requests' }
        },
        {
            path: '/advisor/mdf/marketplace',
            name: 'advisor-mdf-marketplace',
            component: () => import('../views/advisor/AdvisorMdfMarketplace.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'MDF Marketplace' }
        },
        {
            path: '/advisor/mdf/marketplace/:offeringId',
            name: 'advisor-mdf-offering-detail',
            component: () => import('../views/advisor/AdvisorMdfOfferingDetail.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'MDF Marketplace' }
        },
        {
            path: '/advisor/mdf/requests/new',
            name: 'advisor-mdf-new',
            component: () => import('../views/advisor/AdvisorMdfRequestForm.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'New request – MDF Requests' }
        },
        {
            path: '/advisor/mdf/requests/:id/edit',
            name: 'advisor-mdf-request-edit',
            component: () => import('../views/advisor/AdvisorMdfRequestForm.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Edit request – MDF Requests' }
        },
        {
            path: '/advisor/mdf/requests/:id',
            name: 'advisor-mdf-detail',
            component: () => import('../views/advisor/AdvisorMdfRequestDetail.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'MDF Requests' }
        },
        {
            path: '/advisor/suppliers/compare',
            name: 'advisor-supplier-compare',
            component: () => import('../views/AdvisorSupplierCompare.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Compare – Suppliers' }
        },
        {
            path: '/advisor/suppliers/:supplierId',
            name: 'advisor-supplier-profile',
            component: AdvisorSupplierProfile,
            beforeEnter: customAuthGuard,
            meta: { title: 'Suppliers' }
        },
        {
            path: '/advisor/products',
            name: 'advisor-products',
            component: AdvisorProducts,
            beforeEnter: customAuthGuard,
            meta: { title: 'Products' }
        },
        {
            path: '/advisor/products/:productId',
            name: 'advisor-product-detail',
            component: () => import('../views/AdvisorProductDetail.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Products' }
        },
        {
            path: '/advisor/training',
            name: 'advisor-training',
            component: AdvisorTraining,
            beforeEnter: customAuthGuard,
            meta: { title: 'Training' }
        },
        // Legacy meeting/action routes — workspace flow handles these now
        { path: '/advisor/meetings', redirect: '/advisor/today' },
        { path: '/advisor/meetings/legacy', redirect: '/advisor/today' },
        { path: '/advisor/meetings/:meetingId', redirect: '/advisor/today' },
        { path: '/advisor/action-items', redirect: '/advisor/today' },
        {
            path: '/advisor/product-explorer',
            name: 'advisor-product-explorer',
            component: () => import('../views/AdvisorProductExplorer.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Product Explorer' }
        },
        {
            path: '/advisor/search',
            name: 'advisor-search',
            component: () => import('../views/AdvisorSearchResults.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Search' }
        },
        {
            path: '/advisor/ai',
            redirect: '/advisor/today'
        },
        // Legacy: /advisor/ai-classic removed — use /advisor/today
        { path: '/advisor/ai-classic', redirect: '/advisor/today' },
        // Legacy: /advisor/ai-results removed — use /advisor/product-explorer
        { path: '/advisor/ai-results', redirect: '/advisor/product-explorer' },

        // Contacts is the only advisor CRM page still in the nav
        {
            path: '/advisor/contacts',
            name: 'advisor-contacts',
            component: () => import('../views/AdvisorContacts.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Contacts' }
        },
        {
            path: '/advisor/broadband',
            name: 'advisor-broadband',
            component: () => import('../views/advisor/AdvisorBroadbandLookup.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Broadband Lookup' }
        },
        // Legacy CRM redirects — replaced by the workspace flow
        { path: '/advisor/accounts', redirect: '/advisor/workspace/accounts' },
        { path: '/advisor/companies', redirect: '/advisor/workspace/accounts' },
        { path: '/advisor/opportunities', redirect: '/advisor/workspace/deals' },
        { path: '/advisor/deals', redirect: '/advisor/workspace/deals' },
        { path: '/advisor/channel-contacts', redirect: '/advisor/contacts' },
        // v3b-full Mockups — Complete Channel Advisor Workspace (18 screens)
        {
            path: '/advisor/v3b-full/pipeline',
            name: 'v3b-full-pipeline',
            component: () => import('../views/mockups/v3b-full/PipelineView.vue'),
            meta: { title: 'Mockup: Pipeline', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/accounts',
            name: 'v3b-full-accounts',
            component: () => import('../views/mockups/v3b-full/AccountsList.vue'),
            meta: { title: 'Mockup: Accounts', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/account/:accountId',
            name: 'v3b-full-account',
            component: () => import('../views/mockups/v3b-full/AccountWorkspace.vue'),
            meta: { title: 'Mockup: Account', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/account/:accountId/activity/:activityId',
            name: 'v3b-full-activity',
            component: () => import('../views/mockups/v3b-full/ActivityDetail.vue'),
            meta: { title: 'Mockup: Activity', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/account/:accountId/create-deal',
            name: 'v3b-full-create-deal',
            component: () => import('../views/mockups/v3b-full/CreateDeal.vue'),
            meta: { title: 'Mockup: Create deal', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId',
            name: 'v3b-full-deal',
            component: () => import('../views/mockups/v3b-full/DealWorkspace.vue'),
            meta: { title: 'Mockup: Deal', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/confirm-answers',
            name: 'v3b-full-confirm-answers',
            component: () => import('../views/mockups/v3b-full/AnswerConfirmation.vue'),
            meta: { title: 'Mockup: Confirm answers', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/prep-brief',
            name: 'v3b-full-prep-brief',
            component: () => import('../views/mockups/v3b-full/DealPrepBrief.vue'),
            meta: { title: 'Mockup: Prep brief', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/track/:trackId/solution-discovery',
            name: 'v3b-full-solution-discovery',
            component: () => import('../views/mockups/v3b-full/SolutionDiscovery.vue'),
            meta: { title: 'Mockup: Solution discovery', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/track/:trackId/technology-discovery',
            name: 'v3b-full-technology-discovery',
            component: () => import('../views/mockups/v3b-full/TechnologyDiscovery.vue'),
            meta: { title: 'Mockup: Technology discovery', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/track/:trackId/quote-request',
            name: 'v3b-full-quote-request',
            component: () => import('../views/mockups/v3b-full/QuoteRequest.vue'),
            meta: { title: 'Mockup: Quote request', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/track/:trackId/deal-registration',
            name: 'v3b-full-deal-registration',
            component: () => import('../views/mockups/v3b-full/DealRegistration.vue'),
            meta: { title: 'Mockup: Deal registration', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/track/:trackId/quote-management',
            name: 'v3b-full-quote-management',
            component: () => import('../views/mockups/v3b-full/QuoteManagement.vue'),
            meta: { title: 'Mockup: Quote management', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/proposal',
            name: 'v3b-full-proposal',
            component: () => import('../views/mockups/v3b-full/ProposalView.vue'),
            meta: { title: 'Mockup: Proposal', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/config-builder',
            name: 'v3b-full-config-builder',
            component: () => import('../views/mockups/v3b-full/ConfigurationBuilder.vue'),
            meta: { title: 'Mockup: Configuration builder', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/config-compare',
            name: 'v3b-full-config-compare',
            component: () => import('../views/mockups/v3b-full/ConfigurationComparison.vue'),
            meta: { title: 'Mockup: Configuration comparison', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/signals',
            name: 'v3b-full-signals',
            component: () => import('../views/mockups/v3b-full/SignalDashboard.vue'),
            meta: { title: 'Mockup: Signals', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/ai-companion',
            name: 'v3b-full-ai-companion',
            component: () => import('../views/mockups/v3b-full/AiCompanion.vue'),
            meta: { title: 'Mockup: AI companion', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/account/:accountId/context',
            name: 'v3b-full-account-context',
            component: () => import('../views/mockups/v3b-full/AccountContext.vue'),
            meta: { title: 'Mockup: Account context', requiresAuth: false }
        },
        {
            path: '/advisor/v3b-full/deal/:dealId/context',
            name: 'v3b-full-deal-context',
            component: () => import('../views/mockups/v3b-full/DealContext.vue'),
            meta: { title: 'Mockup: Deal context', requiresAuth: false }
        },

        {
            path: '/advisor/metrics',
            name: 'advisor-metrics',
            component: () => import('../views/AdvisorMetrics.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Metrics' }
        },
        {
            path: '/advisor/settings',
            name: 'advisor-settings',
            component: () => import('../views/AdvisorSettings.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Settings' }
        },
        // Legacy quote routes — workspace deal/quote flow replaces these
        { path: '/advisor/quotes', redirect: '/advisor/workspace/deals' },
        { path: '/advisor/quotes/new', redirect: '/advisor/workspace/deals' },
        { path: '/advisor/quotes/:quoteId', redirect: '/advisor/workspace/deals' },
        { path: '/advisor/opportunities/:opportunityId/compare-quotes', redirect: '/advisor/workspace/deals' },

        // --- CHANNEL MANAGER ROUTES ---
        // The CM persona is temporarily disabled: a global guard below sends
        // every /cm/* route (including signup) to the maintenance gate.
        {
            path: '/cm/maintenance',
            name: 'cm-maintenance',
            component: () => import('../views/cm/CmMaintenance.vue'),
            meta: { title: 'Unavailable' }
        },
        {
            path: '/cm/signup',
            name: 'cm-signup',
            component: () => import('../views/cm/CmSignup.vue'),
            meta: { title: 'Sign up' }
        },
        {
            path: '/cm',
            redirect: '/cm/today'
        },
        {
            path: '/cm/today',
            name: 'cm-today',
            component: () => import('../views/cm/CmCommandCenter.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Command Center' }
        },
        {
            path: '/cm/relationships',
            name: 'cm-relationships',
            component: () => import('../views/cm/CmRelationships.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Relationships' }
        },
        {
            path: '/cm/pipeline',
            name: 'cm-pipeline',
            component: () => import('../views/cm/CmPipeline.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Pipeline' }
        },
        {
            path: '/cm/intelligence',
            name: 'cm-intelligence',
            component: () => import('../views/cm/CmIntelligence.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Intelligence' }
        },
        {
            path: '/cm/deal/:id',
            name: 'cm-deal-detail',
            component: () => import('../views/cm/CmDealDetail.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Pipeline' }
        },
        {
            path: '/cm/partner/:id',
            name: 'cm-partner-detail',
            component: () => import('../views/cm/CmPartnerDetail.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Relationships' }
        },
        {
            path: '/cm/tasks',
            name: 'cm-tasks',
            component: () => import('../views/cm/CmTasks.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Tasks' }
        },
        {
            path: '/cm/reports',
            name: 'cm-reports',
            component: () => import('../views/cm/CmReports.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Reports' }
        },
        {
            path: '/cm/settings',
            name: 'cm-settings',
            component: () => import('../views/cm/CmSettings.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Settings' }
        },

        // --- SHARED RESOURCES ---
        {
            path: '/connect-ai',
            name: 'connect-ai',
            component: () => import('../views/ConnectAi.vue'),
            beforeEnter: customAuthGuard,
            meta: { title: 'Connect AI', requiredTier: 'BASIC' }
        },
        {
            path: '/resources/docs',
            name: 'resources-docs',
            component: ResourcesDocs,
            beforeEnter: customAuthGuard,
            meta: { title: 'Documentation – Resources' }
        },
        {
            path: '/resources/support',
            name: 'resources-support',
            component: ResourcesSupport,
            beforeEnter: customAuthGuard,
            meta: { title: 'Support – Resources' }
        },

        // --- SWEEP (connectivity workspace; gated by features.sweep) ---
        ...sweepRoutes.map(r => ({ ...r, beforeEnter: customAuthGuard })),
        ...sweepPublicRoutes,

        // --- 404 (must stay last) ---
        // Public so the terms guard never bounces a bad URL to /terms; the
        // session is still resolved (no redirect) so signed-in users get AppLayout.
        {
            path: '/:pathMatch(.*)*',
            name: 'not-found',
            component: () => import('../views/NotFound.vue'),
            beforeEnter: async () => { await resolveSession(); return true },
            meta: { title: 'Page not found', public: true }
        }
    ]
})

// Channel Manager persona is temporarily disabled — every /cm/* route
// (signup included) lands on the maintenance gate. Admin impersonation
// bypasses so the team can still inspect the console.
router.beforeEach((to) => {
    const isCmPath = to.path === '/cm' || to.path.startsWith('/cm/')
    if (!isCmPath || to.path === '/cm/maintenance') return true

    const userStore = useUserStore()
    if (userStore.isImpersonating) return true

    return { path: '/cm/maintenance' }
})

// Channel Manager (MDF) guard — MDF-only supplier users live on the MDF pages.
router.beforeEach((to) => {
    if (!to.path.startsWith('/supplier')) return true
    const userStore = useUserStore()
    if (!userStore.isMdfOnly) return true
    if (to.path.startsWith('/supplier/mdf')) return true
    return { path: '/supplier/mdf/requests' }
})

// Tier-based route guard — redirect if supplier lacks required tier
export const tierRank: Record<string, number> = { FREE: 0, BASIC: 1, ENTERPRISE: 2 }

router.beforeEach((to) => {
    const requiredTier = to.meta.requiredTier as string | undefined
    if (!requiredTier) return true

    const userStore = useUserStore()

    // Admin impersonation bypasses tier checks
    if (userStore.isImpersonating) return true

    // Only applies to supplier users
    if (!userStore.isSupplier) return true

    const currentTier = (userStore.supplierTier as string) ?? 'FREE'
    if ((tierRank[currentTier] ?? 0) < (tierRank[requiredTier] ?? 0)) {
        // FREE tier lands on profile since dashboard is BASIC+
        return { path: currentTier === 'FREE' ? '/supplier/profile' : '/supplier' }
    }

    return true
})

// Gated features (today: Sweep). A route carrying meta.feature is only
// reachable by a user the backend granted it to — the same predicate that gates
// /api/sweep/*, delivered on the auth/me response — so the sidebar, the routes
// and the API can never disagree. Users without it are sent to their own
// landing page rather than shown a locked door.
router.beforeEach(async (to) => {
    const feature = to.meta.feature as string | undefined
    if (!feature) return true

    const userStore = useUserStore()
    // A global guard runs before the route's own customAuthGuard, so on a cold
    // load (deep link, refresh) the user is not fetched yet and every gated
    // route would bounce. Resolve the session first, then decide.
    if (!userStore.user) {
        await resolveSession()
        if (!userStore.user) return true   // not signed in: let customAuthGuard send them to login
    }

    const features = (userStore.user as { features?: Record<string, boolean> } | null)?.features
    if (features?.[feature]) return true

    return { path: userStore.getDefaultRoute() }
})

// Terms & Conditions guard — a signed-in user who has not accepted the active
// terms version goes to /terms before anything else (including Set Password).
// Acceptance is per version: a returning user on the current version never
// sees it. Skipped while impersonating — the admin accepted on their own row.
let activeTermsVersion: string | null = null
async function getActiveTermsVersion(): Promise<string> {
    if (!activeTermsVersion) {
        try {
            const res = await api.get('/api/legal/terms')
            activeTermsVersion = (res.data?.version as string) || CURRENT_TERMS_VERSION
        } catch {
            activeTermsVersion = CURRENT_TERMS_VERSION
        }
    }
    return activeTermsVersion
}
const TERMS_EXEMPT_PATHS = ['/terms', '/login', '/logout', '/', '/auth/callback', '/signup', '/signup/complete-oauth', '/invite', '/claim']
router.beforeEach(async (to) => {
    // Public pages (shared proposals, mockups) never gate on terms.
    if (to.meta.public === true || to.meta.requiresAuth === false) return true

    const userStore = useUserStore()
    if (!userStore.user) {
        // Cold load (refresh/deep link): resolve the session first so the
        // guard can see the user; unauthenticated users fall through.
        await resolveSession()
        if (!userStore.user) return true
    }
    // Accepting while impersonating would write to the target user's row.
    if (userStore.isImpersonating) return to.path === '/terms' ? { path: userStore.getDefaultRoute(), replace: true } : true

    const version = await getActiveTermsVersion()
    const { termsAcceptedAt, termsVersionAccepted } = userStore.user
    const pending = !termsAcceptedAt || termsVersionAccepted !== version

    if (to.path === '/terms') return pending ? true : { path: userStore.getOnboardingRoute(), replace: true }
    if (pending && !TERMS_EXEMPT_PATHS.includes(to.path)) return { path: '/terms', replace: true }
    return true
})

// Force password change guard — redirect if user must change password
router.beforeEach((to) => {
    const allowedPaths = ['/change-password', '/terms', '/login', '/logout', '/', '/auth/callback']
    if (allowedPaths.includes(to.path)) return true

    const userStore = useUserStore()
    if (userStore.user?.mustChangePassword) {
        return { path: '/change-password' }
    }

    return true
})

router.afterEach((to, from, failure) => {
    trackPageView(to.path, (to.name as string) ?? undefined)

    // Static tab title from meta.title; detail views refine it with usePageTitle.
    // Query/hash-only changes (tabs, filters) keep the current title.
    if (failure || (to.path === from.path && from.matched.length)) return
    if (import.meta.env.DEV && !to.meta.title) console.warn(`[router] route "${String(to.name ?? to.path)}" has no meta.title`)
    setRouteTitle(to.meta.title as string | undefined)
})

export default router
